import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

/**
 * Detect whether the user has an active Supabase session by sniffing
 * localStorage. We don't useAuth() here because AuthProvider lives
 * inside this ErrorBoundary's children — when the boundary renders the
 * fallback, it's outside AuthProvider's tree.
 *
 * Supabase persists the session under a key like `sb-<project-ref>-auth-token`.
 */
function hasSupabaseSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const value = localStorage.getItem(key);
        if (value && value !== 'null' && value !== '""') return true;
      }
    }
  } catch {
    // localStorage access can throw in private mode — treat as no session.
  }
  return false;
}

function ErrorFallback({ resetError }: { resetError: () => void }) {
  const navigate = useNavigate();
  const signedIn = hasSupabaseSession();
  const target = signedIn ? '/dashboard' : '/';
  const label = signedIn ? 'Return to Dashboard' : 'Return to Home';

  // SPA recovery: clear the boundary's error state, then navigate via
  // react-router. No window.location, no full reload — the in-memory app
  // state stays warm and the destination renders instantly.
  const handleClick = () => {
    resetError();
    navigate(target);
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: '2rem', textAlign: 'center',
      background: 'var(--color-bg-primary, #0a0e1a)', color: 'var(--color-text-primary, #fff)',
    }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
      <p style={{ color: 'var(--color-text-secondary, #888)', marginBottom: '1.5rem' }}>
        The app encountered an unexpected error.
      </p>
      <button
        onClick={handleClick}
        style={{
          padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none',
          background: 'var(--color-accent, #7db8f2)', color: '#000',
          fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
        }}
      >
        {label}
      </button>
    </div>
  );
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      // Function-form fallback so we can pull resetError out and pass it
      // to the inner component, where the useNavigate hook can be used.
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
      onError={(error, componentStack) => {
        console.error('App error:', error, componentStack);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
